import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import type { FormatoImpressao } from '@/features/marcacao/formato';
import type { RespostaMarcacao } from '@/features/marcacao/tipos';
import type { ChaveDocumento } from '@/features/historico-paciente/tipos';
import { ImpressaoMarcacao } from '@/components/marcacao/impressao-marcacao';
import { Botao } from '@/components/ui/button';

/**
 * Comprovante de uma marcação do eSUS aberto pelo histórico do paciente, para
 * quem não tem solicitação no KAVI.
 *
 * Fica na própria tela do histórico, e não numa rota: o CPF/CNS consultado
 * não vai para a URL, e voltar devolve a lista como estava, sem redigitar o
 * documento. O documento segue junto do identificador para o backend
 * conferir que a marcação é mesmo deste paciente.
 */
export function MarcacaoDoHistorico({
  documento,
  identificador,
  chaveConfirmacao,
  aoVoltar,
}: {
  documento: ChaveDocumento;
  identificador: string;
  chaveConfirmacao: string | null;
  aoVoltar: () => void;
}) {
  const [formato, setFormato] = React.useState<FormatoImpressao>('termica');

  const consulta = useQuery({
    queryKey: ['marcacao-historico', identificador, documento],
    queryFn: () =>
      api.post<RespostaMarcacao>('/historico-paciente/esus/marcacao', {
        ...documento,
        identificador,
      }),
    // Cada abertura é uma consulta auditada ao eSUS: nada de repetir sozinha.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  return (
    <ImpressaoMarcacao
      consulta={consulta}
      formato={formato}
      aoEscolherFormato={setFormato}
      chaveConfirmacao={chaveConfirmacao}
      voltar={
        <Botao variante="fantasma" tamanho="sm" className="-ml-3" onClick={aoVoltar}>
          <ArrowLeft aria-hidden="true" />
          Voltar ao histórico
        </Botao>
      }
    />
  );
}
